import React from "react";
import { MathJax } from "better-react-mathjax";

export function LinearFitIntro({ lite = false }) {
  const baseClass = lite
    ? 'space-y-2 text-justify hyphens-auto text-sm leading-relaxed text-white/80 md:text-base'
    : 'space-y-4 text-justify hyphens-auto leading-relaxed text-white/85 text-sm md:text-base';
  return (
    <div className={baseClass}>
      Polynomial least-squares fitting approximates an unknown relationship by a low-degree polynomial
      that minimizes the mismatch between observed data and model predictions in the Euclidean norm.
      Given data <MathJax inline>{"\\( (x_i,y_i) \\)"}</MathJax>, we seek a 
      polynomial <MathJax inline>{"\\( p_d(x)=\\sum_{k=0}^{d} c_k x^k \\)"}</MathJax> that minimizes the sum of squared residuals.
      This approach provides a smooth, analytically tractable surrogate for noisy measurements and
      underpins methods in scientific computing, data analysis, signal processing, and system identification.
      Because evaluation and differentiation of polynomials are inexpensive, the resulting fit is easy to
      deploy for prediction, filtering, and real-time control, while offering a transparent bias–variance
      trade-off through the choice of the degree <MathJax inline>{"\\( d \\)"}</MathJax>.
    </div>
  );
}

export default function LinearFitLongExplanation() {
  return (
    <div className="space-y-6 text-sm leading-relaxed text-white/80 text-justify hyphens-auto md:text-base">
      <h2 className="text-sm font-semibold md:text-base">1. Problem Statement & Motivation</h2>
      <p>
        Given <MathJax inline>{"\\( m \\)"}</MathJax> data points <MathJax inline>{"\\( (x_i,y_i) \\)"}</MathJax> with
        (typically) <MathJax inline>{"\\( m\\gg d+1 \\)"}</MathJax>, find coefficients <MathJax inline>{"\\( c=(c_0,\\dots,c_d)^\\top \\)"}</MathJax> that minimize
      </p>
      <MathJax>{String.raw`\[
        \min_{c\in\mathbb{R}^{d+1}} \; \sum_{i=1}^m \big( y_i - p_d(x_i) \big)^2
        \;=\; \min_c \; \|A c - y\|_2^2,
      \]`}</MathJax>
      <p>
        where <MathJax inline>{"\\( A\\in\\mathbb{R}^{m\\times(d+1)} \\)"}</MathJax> is the (possibly scaled) Vandermonde-like 
        design matrix <MathJax inline>{"\\( A_{ik}=\\phi_k(x_i) \\)"}</MathJax> for a chosen basis <MathJax inline>{"\\( \\{\\phi_k\\}_{k=0}^d \\)"}</MathJax> (monomials, shifted/scaled monomials, or orthogonal polynomials).
        The overdetermined setting reflects noisy or redundant measurements; the objective enforces an
        <em> orthogonal projection</em> of the data onto the polynomial model class. Choosing a modest degree
        controls variance while capturing dominant trends, making the method robust and interpretable.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">2. Normal Equations & Projection View</h2>
      <p>
        The least-squares solution (when columns of <MathJax inline>{"\\( A \\)"}</MathJax> are linearly independent)
        solves the <em>normal equations</em>:
      </p>
      <MathJax>{String.raw`\[
        A^{\top}A\,c \;=\; A^{\top}y.
      \]`}</MathJax>
      <p>
        Geometrically, <MathJax inline>{"\\( Ac \\)"}</MathJax> is the orthogonal projection of <MathJax inline>{"\\( y \\)"}</MathJax> onto the column space
        of <MathJax inline>{"\\( A \\)"}</MathJax>. The residual <MathJax inline>{"\\( r=y-Ac \\)"}</MathJax> is orthogonal to that space,
        i.e., <MathJax inline>{"\\( A^{\\top}r=0 \\)"}</MathJax>. The <em>hat matrix</em> <MathJax inline>{"\\( H=A(A^{\\top}A)^{-1}A^{\\top} \\)"}</MathJax> maps data 
        to the fits: <MathJax inline>{"\\( \\hat y = Hy \\)"}</MathJax>, residuals <MathJax inline>{"\\( r=(I-H)y \\)"}</MathJax>. 
        The matrix <MathJax inline>{"\\( H \\)"}</MathJax> is symmetric and idempotent, and its trace equals the 
        model dimension <MathJax inline>{"\\( d+1 \\)"}</MathJax>, reflecting the effective number of parameters. While the normal equations expose
        this projection viewpoint, they also square the condition number, motivating numerically safer solvers.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">3. Numerical Solution: QR and SVD</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <strong>QR factorization:</strong> Compute <MathJax inline>{"\\( A=QR \\)"}</MathJax> with <MathJax inline>{"\\( Q^\\top Q=I \\)"}</MathJax>, <MathJax inline>{"\\( R \\)"}</MathJax> upper triangular, 
          and solve <MathJax inline>{"\\( Rc = Q^\\top y \\)"}</MathJax>. Householder QR is the standard choice: 
          it avoids forming <MathJax inline>{"\\( A^{\\top}A \\)"}</MathJax>, is backward stable, and handles moderately ill-conditioned problems well.
          With column pivoting (RRQR), it also detects rank deficiency and provides a principled model truncation.
        </li>
        <li>
          <strong>SVD:</strong> For <MathJax inline>{"\\( A=U\\Sigma V^\\top \\)"}</MathJax>, 
          the solution <MathJax inline>{"\\( c = V\\Sigma^\\dagger U^\\top y \\)"}</MathJax> is the minimum-norm least-squares solution.
          SVD reveals numerical rank, isolates near-null directions, and enables truncated or damped solutions for
          ill-posed settings. It is the most robust (though costlier) approach, preferred when conditioning is poor or
          the basis is high degree.
        </li>
        <li>
          <strong>Normal equations:</strong> Algebraically simple but less stable, 
          since <MathJax inline>{"\\( \\kappa(A^{\\top}A)=\\kappa(A)^2 \\)"}</MathJax>. Use only 
          when <MathJax inline>{"\\( A \\)"}</MathJax> is well-conditioned and dimensions are small.
        </li>
      </ul>

      <h2 className="text-sm font-semibold mt-4 md:text-base">4. Choice of Basis & Scaling</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <strong>Monomials</strong> <MathJax inline>{"\\( 1,x,x^2,\\ldots \\)"}</MathJax> are simple but can be badly conditioned on wide
          intervals or for large <MathJax inline>{"\\( d \\)"}</MathJax>. Spurious oscillations and large coefficient magnitudes are common.
        </li>
        <li>
          <strong>Shift/scale</strong> <MathJax inline>{"\\( x\\mapsto \\tilde x=(x-\\mu)/s \\)"}</MathJax> to roughly <MathJax inline>{"\\( [-1,1] \\)"}</MathJax> to 
          reduce dynamic range. Centering and scaling substantially improve the
          conditioning of <MathJax inline>{"\\( A \\)"}</MathJax>, regardless of basis.
        </li>
        <li>
          <strong>Orthogonal polynomials:</strong> Legendre/Chebyshev on <MathJax inline>{"\\( [-1,1] \\)"}</MathJax> yield well-conditioned
          designs and stable recurrences. Chebyshev polynomials are particularly robust and admit efficient evaluation
          via Clenshaw recurrences.
        </li>
        <li>
          <strong>Stable evaluation:</strong> Evaluate <MathJax inline>{"\\( p_d \\)"}</MathJax> via Horner (monomials) or Clenshaw (orthogonal bases)
          to limit rounding error and cost to <MathJax inline>{"\\( \\mathcal{O}(d) \\)"}</MathJax> per point.
        </li>
      </ul>

      <h2 className="text-sm font-semibold mt-4 md:text-base">5. Weighted & Constrained Variants</h2>
      <p>
        If measurements have heterogeneous noise or varying reliability, use <em>weighted least squares</em> with
        diagonal weights <MathJax inline>{"\\( W=\\operatorname{diag}(w_i) \\)"}</MathJax> (typically <MathJax inline>{"\\( w_i\\propto \\sigma_i^{-2} \\)"}</MathJax>):
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        \min_c \; &\|W^{1/2}(Ac-y)\|_2^2\\
        &\Rightarrow (A^{\top}WA)c=A^{\top}Wy.
        \end{aligned}
      \]`}</MathJax>
      <p>
        Linear constraints—value constraints such as <MathJax inline>{"\\( p(a)=\\alpha \\)"}</MathJax>, slope constraints 
        such as <MathJax inline>{"\\( p'(b)=\\beta \\)"}</MathJax>, or general linear functionals—can be imposed with Lagrange multipliers,
        leading to small KKT systems, or by eliminating constrained coefficients through basis modification.
        These mechanisms allow one to encode boundary behavior, monotonicity proxies, or calibration conditions
        without sacrificing the convexity or efficiency of the core least-squares problem.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">6. Regularization (Tikhonov / Smoothing)</h2>
      <p>
        To combat overfitting and mitigate ill-conditioning, augment the objective with a quadratic penalty:
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        &\min_c \; \|Ac-y\|_2^2 + \lambda \|L c\|_2^2\\
        &\Rightarrow (A^{\top}A + \lambda L^{\top}L)c = A^{\top}y.
        \end{aligned}
      \]`}</MathJax>
      <p>
        Common choices include <MathJax inline>{"\\( L=I \\)"}</MathJax> (ridge) to shrink coefficients uniformly, or
        discrete derivative operators (e.g., penalizing high-order coefficients) to encourage smoothness of the fit.
        The parameter <MathJax inline>{"\\( \\lambda \\)"}</MathJax> controls the bias–variance trade-off; it can be selected by
        cross-validation, generalized cross-validation, or L-curve heuristics. In orthogonal bases, the effect of
        Tikhonov regularization is particularly transparent: high-degree components are damped first, stabilizing
        extrapolation and improving predictive performance in the presence of noise.
      </p>
    </div>
  );
}
